import { useEffect, useRef } from 'react';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { networkStatusSubject } from './useNetworkListener';
import { localComms, isLocalCommsEligible } from '../../utils/localComms';
import Sentry from '../../utils/Sentry/Sentry';

const RECONNECT_SYNC_DELAY_MS = 2000;

/**
 * Local comms (orders-sdk) persists events it couldn't push to the cloud
 * while offline and can flush that queue via syncPendingEvents(), but
 * nothing was ever calling it. Trigger a flush as soon as connectivity
 * comes back instead of relying solely on the SDK's own internal schedule.
 */
export const useAutoSyncOnReconnect = () => {
  const { value: isConnected } = useBehaviorSubjectState(networkStatusSubject);
  const previousConnectionStatus = useRef(isConnected);

  useEffect(() => {
    const wasOffline = previousConnectionStatus.current === false;
    previousConnectionStatus.current = isConnected;

    if (!wasOffline || isConnected !== true || !isLocalCommsEligible()) {
      return;
    }

    // Small delay to let the network settle before attempting the sync.
    const timeoutId = setTimeout(async () => {
      try {
        await localComms.syncPendingEvents();
      } catch (error) {
        Sentry.captureException(error, {
          extra: { context: 'local-comms-sync-pending-events-on-reconnect' },
        });
      }
    }, RECONNECT_SYNC_DELAY_MS);

    return () => clearTimeout(timeoutId);
  }, [isConnected]);
};
